import { isNoDoerAssignedError } from '@doneverse/data/api'
import { adminFeedbackThreadOptions, feedbackListOptions, feedbackThreadOptions } from '@doneverse/data/hooks'
import {
  assignedProjectsDetailedOptions,
  blueprintStepsOptions,
  masterPlaybooksOptions,
  meFoundersOptions,
  meLoyaltyOptions,
  workspaceDetailedOptions,
  playbookDetailOptions,
  playbookBriefOptions,
  businessBriefOptions,
  foundersLoungeCatalogOptions,
  foundersLoungeArticleOptions,
  foundationsCourseOptions,
  masterclassVaultCatalogOptions,
  masterclassVaultCourseOptions,
  doersUniversityCatalogOptions,
  contactsOptions,
  messagesInfiniteOptions,
  taskDetailOptions,
  labelCountsOptions,
  labelListOptions,
  taskListInfiniteOptions,
  taskListOptions,
  groupsListOptions,
  groupDetailOptions,
  groupMembersOptions,
  groupPostsInfiniteOptions,
  userMentionsOptions,
  userRepliesOptions,
  coachingCallsListOptions,
  launchChecklistOptions,
  launchDayOptions,
  doerProfileOptions,
  workspaceDoerProfilesOptions,
} from '@doneverse/data/queries'
import { notificationService } from '@doneverse/data/services'
import type { TaskStatus, UserRole } from '@doneverse/shared'
import type { QueryClient } from '@tanstack/react-query'
import {
  createRouter,
  createRootRouteWithContext,
  createRoute,
  redirect,
  lazyRouteComponent,
  HeadContent,
  Outlet,
} from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { createPortal } from 'react-dom'
import { validateActivityLogSearch } from '@/components/admin/activity/activityLogSearch'
import { validateActivitySearchSearch } from '@/components/admin/activity/activitySearchSearch'
import { validateActivitySummarySearch } from '@/components/admin/activity/activitySummarySearch'
import { MasterclassEditorSkeleton } from '@/components/admin/cms/MasterclassEditorShell'
import { PlaybookEditorSkeleton } from '@/components/admin/cms/PlaybookEditorShell'
import { AuthenticatedUserProvider } from '@/components/auth/AuthenticatedUserContext'
import { CoachGraceWidget } from '@/components/chat/CoachGraceWidget'
import { RouteErrorBoundary } from '@/components/error/RouteErrorBoundary'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { GroupsLayout } from '@/components/layout/GroupsLayout'
import { IconRailLayout } from '@/components/layout/IconRailLayout'
import { SettingsLayout } from '@/components/layout/SettingsLayout'
import {
  PlaybookAccessErrorBoundary,
  PlaybookAccessLayout,
} from '@/components/playbook/PlaybookAccessErrorBoundary'
import {
  DoerHomePending,
  DoingBlueprintPending,
  FilesPending,
  PlaybooksPending,
  PlaybookDetailPending,
  BusinessBriefPending,
  MessagesPending,
  TeamsPending,
  SettingsPending,
  ActivityAgentPending,
  MyActivityPending,
  MyTeamProjectsPending,
  AdminUsersPending,
  AdminCoachingSessionsPending,
  AdminEscalationsPending,
  AdminCategoriesPending,
  AdminPlaybooksPending,
  AdminTeamsPending,
  AdminTeamDetailPending,
  AdminDoerGroupsPending,
  AdminDoerGroupDetailPending,
  AdminGroupChatsPending,
  AdminGroupChatDetailPending,
  AdminCategoryDetailPending,
  AdminStatsPending,
  StartHerePending,
  NotificationsPending,
  TaskCollectionDetailPending,
  TaskCreatePending,
  TaskDetailPending,
  TasksPending,
  GroupsPending,
  GroupDetailPending,
  GroupInboxPending,
  CoachingCallsPending,
  CoachingCallReplayPending,
  DoersUniversityPending,
  MasterclassVaultPending,
  MasterclassLessonPending,
  FoundationsPending,
  FoundersLoungePending,
  FoundersLoungeArticlePending,
  TechStackFinderPending,
  KickOffCallPending,
} from '@/components/skeletons/route-skeletons'
import type { AuthContextType } from '@/contexts/AuthContext'
import { useAuth } from '@/contexts/AuthContext'
import { CoachGraceProvider } from '@/contexts/CoachGraceContext'
import { useFeatureFlagRouteGuard } from '@/hooks/useFeatureFlagRouteGuard'
import { validateAdminFeedbackSearch } from '@/lib/admin-feedback-search'
import { hasProtectedRouteAuthFailure } from '@/lib/auth-session'
import { buildCoachingCallsWindow } from '@/lib/coaching-calls-window'
import { isAllowedForRestrictedDoer, isRestrictedDoer, RESTRICTED_DOER_HOME } from '@/lib/doerAccess'
import { resolveEnabledFlags } from '@/lib/feature-flags'
import { FEEDBACK_SURFACE_ROLES } from '@/lib/feedback-access'
import { MESSAGING_SURFACE_ROLES } from '@/lib/messaging-access'
import { isAllowedWhileOnboardingLocked, selectOnboardingLock } from '@/lib/onboardingLock'
import { getHomeRoute } from '@/lib/routes'
import {
  resolveProjectListId,
  boardStatuses,
  statusQueryFilters,
  taskBoardFilters,
  validateTasksSearch,
  type TasksSearch,
  type TasksTab,
} from '@/lib/tasks-search'
import { AdminSchemaSkeleton } from '@/pages/admin/AdminPlaybookSchema'
import Callback from '@/pages/Callback'
import ForgotPassword from '@/pages/ForgotPassword'
import Index from '@/pages/Index'
import Logout from '@/pages/Logout'
import NotFound from '@/pages/NotFound'
import Signup from '@/pages/Signup'
import { useDashboardStore } from '@/store'

// Eager imports for critical / small pages

// ─── Router context ───────────────────────────────────────────────
export interface RouterContext {
  queryClient: QueryClient
  auth: AuthContextType
}

// ─── Root route ───────────────────────────────────────────────────
const TITLE_SUFFIX = 'Doneverse - Your Single Source of Truth'

function formatTitle(prefix?: string) {
  return prefix ? `${prefix} | ${TITLE_SUFFIX}` : TITLE_SUFFIX
}

function RouterHead() {
  return createPortal(<HeadContent />, document.head)
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [{ title: formatTitle() }],
  }),
  component: function RootLayout() {
    useFeatureFlagRouteGuard()

    return (
      <>
        <RouterHead />
        <CoachGraceProvider>
          <Outlet />
          <CoachGraceWidget />
        </CoachGraceProvider>
      </>
    )
  },
  errorComponent: RouteErrorBoundary,
  notFoundComponent: NotFound,
})

// ─── Public routes ────────────────────────────────────────────────
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Index,
  validateSearch: (search: Record<string, unknown>) => ({
    invite: (search.invite as string) || undefined,
  }),
  beforeLoad: ({ search }) => {
    if (search.invite) {
      throw redirect({ to: '/signup', search: { invite: search.invite } })
    }
  },
})

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/callback',
  head: () => ({
    meta: [{ title: formatTitle('Authenticating') }],
  }),
  component: Callback,
  validateSearch: (search: Record<string, unknown>) => ({
    error: (search.error as string) || undefined,
    error_description: (search.error_description as string) || undefined,
  }),
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  head: () => ({
    meta: [{ title: formatTitle('Forgot Password') }],
  }),
  component: ForgotPassword,
})

const authRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  beforeLoad: () => {
    throw redirect({ to: '/' })
  },
})

const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logout',
  head: () => ({
    meta: [{ title: formatTitle('Logging Out') }],
  }),
  component: Logout,
})

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: Signup,
  head: () => ({
    meta: [{ title: formatTitle('Sign Up') }],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    invite: (search.invite as string) || undefined,
  }),
})

// Permanent redirects from the retired /admin/playground routes to their new
// /workflows/social-media-calendar homes. Kept as a courtesy for any stale
// bookmarks; safe to delete once external links are confirmed gone.
const adminPlaygroundRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/playground',
  beforeLoad: () => {
    throw redirect({ to: '/workflows/social-media-calendar' })
  },
})

const adminPlaygroundRunRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/playground/runs/$wpId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/workflows/social-media-calendar/runs/$wpId',
      params: { wpId: (params as { wpId: string }).wpId },
    })
  },
})

const adminPlaygroundRunsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/playground/runs',
  beforeLoad: () => {
    throw redirect({ to: '/workflows/social-media-calendar/runs' })
  },
})

// Admin groups is no longer a separate surface — admins browse the unified
// groups view at /groups like everyone else. Keep permanent redirects so old
// /admin/groups* links and bookmarks still land in the right place.
const adminGroupsRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/groups',
  beforeLoad: () => {
    throw redirect({ to: '/groups' })
  },
})

const adminGroupDetailRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/groups/$groupId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/groups/$groupId',
      params: { groupId: (params as { groupId: string }).groupId },
    })
  },
})

// ─── Protected layout route ──────────────────────────────────────
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_protected',
  beforeLoad: ({ context }) => {
    const { isAuthenticated, loading, isLoggingOut } = context.auth
    if (!loading && !isLoggingOut && !isAuthenticated) {
      throw redirect({ to: '/' })
    }
  },
  component: ProtectedLayout,
})

export function ProtectedLayout() {
  const { loading, isLoggingOut, user, profile, authError, authErrorStatus, logout } = useAuth()
  const hasAuthFailure = hasProtectedRouteAuthFailure({
    loading,
    hasUser: !!user,
    hasProfile: !!profile,
    authErrorStatus,
  })

  const hasSessionMismatch = !loading && !isLoggingOut && !user

  if (hasAuthFailure || hasSessionMismatch) {
    const title = authErrorStatus === 403 ? 'Access lost' : 'Session expired'
    const message =
      authError ||
      (authErrorStatus === 403
        ? 'You no longer have access to this dashboard. Sign in again to continue.'
        : hasSessionMismatch
          ? 'We could not verify your session. Sign out and sign in again.'
          : 'Your session expired. Sign in again to continue.')

    return (
      <RouteErrorBoundary
        error={new Error(message)}
        title={title}
        retryLabel={isLoggingOut ? 'Try Sign Out Again' : 'Sign In Again'}
        onRetry={() => void logout().catch(() => {})}
      />
    )
  }

  if (loading || isLoggingOut || !user || !profile) {
    return (
      <div className="flex justify-center items-center h-dvh" data-testid="protected-route-loader">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <AuthenticatedUserProvider user={user} profile={profile}>
      <Outlet />
    </AuthenticatedUserProvider>
  )
}

// ─── Icon rail layout route ──────────────────────────────────────
// The outermost authenticated shell: every dashboard surface, including the
// immersive ones that skip DashboardLayout, hangs off this. The onboarding lock
// is enforced here so it covers all of them at once.
const iconRailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  id: '_iconRail',
  beforeLoad: ({ context, location }) => {
    enforceOnboardingLock(context, location.pathname)
  },
  component: IconRailLayout,
})

// ─── Messages routes (optional param via sibling routes) ─────────
const messagesRoute = createRoute({
  getParentRoute: () => iconRailRoute,
  path: '/messages',
  head: () => ({
    meta: [{ title: formatTitle('Messages') }],
  }),
  beforeLoad: ({ context, location }) => {
    requireRole(context, MESSAGING_SURFACE_ROLES)
    enforceRestrictedDoerAccess(context, location.pathname)
  },
  component: lazyRouteComponent(() => import('@/pages/Messages')),
  pendingComponent: MessagesPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(contactsOptions()).catch(() => {})
  },
})

const messagesContactRoute = createRoute({
  getParentRoute: () => iconRailRoute,
  path: '/messages/$contactId',
  head: () => ({
    meta: [{ title: formatTitle('Messages') }],
  }),
  beforeLoad: ({ context, location }) => {
    requireRole(context, MESSAGING_SURFACE_ROLES)
    enforceRestrictedDoerAccess(context, location.pathname)
  },
  component: lazyRouteComponent(() => import('@/pages/Messages')),
  loader: ({ context: { queryClient, auth }, params: { contactId } }) => {
    if (!auth.isAuthenticated) return
    // Prefetch without awaiting — contacts are already cached from /messages,
    // and messages load in-component via useQuery. Not awaiting prevents
    // the route transition from showing a pendingComponent skeleton flash.
    queryClient.ensureQueryData(contactsOptions()).catch(() => {})
    queryClient.ensureInfiniteQueryData(messagesInfiniteOptions(contactId)).catch(() => {})
  },
})

// ─── Feedback routes (optional param via sibling routes) ─────────
// Its own top-level section beside Messages: threaded replies need the scroll
// space a pop-up can't give them.
const feedbackRoute = createRoute({
  getParentRoute: () => iconRailRoute,
  path: '/feedback',
  head: () => ({
    meta: [{ title: formatTitle('Feedback') }],
  }),
  beforeLoad: ({ context, location }) => {
    if (context.auth.profile?.role === 'admin') throw redirect({ to: '/admin/feedback' })
    requireRole(context, FEEDBACK_SURFACE_ROLES)
    enforceRestrictedDoerAccess(context, location.pathname)
  },
  component: lazyRouteComponent(() => import('@/pages/Feedback')),
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(feedbackListOptions()).catch(() => {})
  },
})

const feedbackItemRoute = createRoute({
  getParentRoute: () => iconRailRoute,
  path: '/feedback/$feedbackId',
  head: () => ({
    meta: [{ title: formatTitle('Feedback') }],
  }),
  beforeLoad: ({ context, location, params: { feedbackId } }) => {
    // Admins submit through the same dialog as everyone else, so another admin's
    // reply notifies them — but their copy of the thread is the queue item. Send
    // them there rather than home, so both the in-app tap and the push URL
    // (`/feedback/<id>`, mapped API-side) land on the thread they were told about.
    if (context.auth.profile?.role === 'admin') {
      throw redirect({ to: '/admin/feedback/$feedbackId', params: { feedbackId } })
    }
    requireRole(context, FEEDBACK_SURFACE_ROLES)
    enforceRestrictedDoerAccess(context, location.pathname)
  },
  component: lazyRouteComponent(() => import('@/pages/Feedback')),
  loader: ({ context: { queryClient, auth }, params: { feedbackId } }) => {
    if (!auth.isAuthenticated) return
    // Not awaited — this is where a notification deep-links to, and the list is
    // usually already cached, so awaiting only buys a skeleton flash.
    queryClient.ensureQueryData(feedbackListOptions()).catch(() => {})
    queryClient.ensureQueryData(feedbackThreadOptions(feedbackId)).catch(() => {})
  },
})

// ─── Role-based access ───────────────────────────────────────────
function requireRole(context: RouterContext, allowedRoles: UserRole[]) {
  const profile = context.auth.profile
  if (profile && !allowedRoles.includes(profile.role)) {
    throw redirect({ to: getHomeRoute(profile.role) })
  }
}

// A restricted doer — no assigned founder, or the `doer-dashboard-hidden`
// override — is limited to Home + Settings (+ Feedback, Doer Profile; see
// lib/doerAccess). Any other founder-scoped surface bounces to Home. No-op for
// every other role and for the allowed paths (Home/Settings never redirect), so
// there's no loop; thrown in beforeLoad → no first-paint flash.
function enforceRestrictedDoerAccess(context: RouterContext, pathname: string) {
  const profile = context.auth.profile
  if (!profile) return
  const flags = resolveEnabledFlags(profile.feature_flags)
  if (isRestrictedDoer(profile, (flag) => flags.includes(flag)) && !isAllowedForRestrictedDoer(pathname)) {
    throw redirect({ to: RESTRICTED_DOER_HOME })
  }
}

// Applies the founder onboarding lock to a navigation. What the lock allows,
// and why, is lib/onboardingLock's to state; this is only where it is enforced.
//
// Enforced on iconRailRoute, the single parent of every authenticated surface,
// rather than repeated per subtree: a new page must not be able to opt out of
// the lock by forgetting to call this. It also catches a URL typed straight
// into the address bar, which no amount of menu-disabling would.
// AuthBootstrapBoundary holds the router until /v1/me has resolved, so
// `profile` is populated on the very first beforeLoad rather than arriving
// after the page has already rendered.
//
// The redirect target is itself always allowed, so there is no loop, and the
// whole thing is a no-op for every other role and for unlocked founders.
function enforceOnboardingLock(context: RouterContext, pathname: string) {
  const profile = context.auth.profile
  if (!profile) return
  const flags = resolveEnabledFlags(profile.feature_flags)
  const lock = selectOnboardingLock(profile, (flag) => flags.includes(flag))
  if (isAllowedWhileOnboardingLocked(pathname, lock)) return
  throw redirect({ to: lock.redirectTo, replace: true })
}

async function ensureWorkspaceAccess(queryClient: QueryClient) {
  try {
    return await queryClient.ensureQueryData(workspaceDetailedOptions())
  } catch (error) {
    if (isNoDoerAssignedError(error)) return null
    throw error
  }
}

// ─── Shared app layout ───────────────────────────────────────────
// One layout route for every dashboard role. Renders DashboardLayout (icon-rail
// sidebar + main) exactly once; per-role access control lives in the small
// Outlet wrappers below (adminOnlyRoute, dashboardRolesRoute, groupsLayoutRoute),
// so admins, founders, doers and the team roles all ride the same shell while
// still only reaching their own pages. It admits every role, so it never
// redirects on its own — the child wrappers do the gating. The restricted-doer
// rule is enforced once here for the whole shell.
const appLayoutRoute = createRoute({
  getParentRoute: () => iconRailRoute,
  id: '_appLayout',
  beforeLoad: ({ context, location }) => {
    requireRole(context, [
      'founder',
      'doer',
      'doing_team',
      'intake_team',
      'admin',
      'escalation_officer',
      'dsl',
    ])
    enforceRestrictedDoerAccess(context, location.pathname)
  },
  component: DashboardLayout,
})

// ─── Admin routes (everything under /admin/*) ────────────────────
// Admin rides the same sectioned shell as founders/doers; its nav lives in
// founderNavConfig (admin-* sections) and renders via AppSidebar.
const adminOnlyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: '_adminOnly',
  beforeLoad: ({ context }) => requireRole(context, ['admin']),
  component: Outlet,
})

// The Activity Log and Activity Summary are the /admin/* pages a DSL may use (the API scopes
// both to their team). They sit under their own guard rather than adminOnlyRoute so every other
// admin route keeps redirecting a DSL.
const activityViewerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: '_activityViewer',
  beforeLoad: ({ context }) => requireRole(context, ['admin', 'dsl']),
  component: Outlet,
})

const adminRedirectRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin',
  beforeLoad: () => {
    throw redirect({ to: '/admin/stats' })
  },
})

const adminStatsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/stats',
  head: () => ({
    meta: [{ title: formatTitle('Admin Stats') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminStats')),
  pendingComponent: AdminStatsPending,
})

const adminActivityLogRoute = createRoute({
  getParentRoute: () => activityViewerRoute,
  path: '/admin/activity-log',
  head: () => ({
    meta: [{ title: formatTitle('Activity Log') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminActivityLog')),
  // Lives next to the page rather than inline here, so the URL contract has
  // somewhere to be unit-tested. The legacy `?idle=true` mapping in particular
  // is the kind of rule that gets deleted as dead code when nothing asserts it.
  validateSearch: validateActivityLogSearch,
})

const adminActivitySearchRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/activity-search',
  head: () => ({
    meta: [{ title: formatTitle('Activity Search') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminActivitySearch')),
  validateSearch: validateActivitySearchSearch,
})

const adminActivitySummaryRoute = createRoute({
  getParentRoute: () => activityViewerRoute,
  path: '/admin/activity-summary',
  head: () => ({
    meta: [{ title: formatTitle('Activity Summary') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminActivitySummary')),
  validateSearch: validateActivitySummarySearch,
})

const adminActivityClassificationsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/activity-classifications',
  head: () => ({ meta: [{ title: formatTitle('Activity Classifications') }] }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminActivityClassifications')),
})

const adminAgentReleasesRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/agent-releases',
  head: () => ({ meta: [{ title: formatTitle('Agent Releases') }] }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminAgentReleases')),
})

const adminUsersRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/users',
  head: () => ({
    meta: [{ title: formatTitle('Admin Users') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminUsers')),
  pendingComponent: AdminUsersPending,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string
    page?: number
    sort?: string
    order?: 'asc' | 'desc'
    unassigned?: boolean
    status?: 'active' | 'inactive'
  } => {
    const result: {
      q?: string
      page?: number
      sort?: string
      order?: 'asc' | 'desc'
      unassigned?: boolean
      status?: 'active' | 'inactive'
    } = {}
    if (typeof search.q === 'string' && search.q) result.q = search.q
    if (typeof search.page === 'number' && search.page > 1) result.page = search.page
    if (typeof search.sort === 'string' && search.sort) result.sort = search.sort
    if (search.order === 'asc' || search.order === 'desc') result.order = search.order
    if (search.unassigned === true) result.unassigned = true
    if (search.status === 'active' || search.status === 'inactive') result.status = search.status
    return result
  },
})

const adminUserDetailRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/users/$userId',
  head: () => ({
    meta: [{ title: formatTitle('User Detail') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminUserDetail')),
  pendingComponent: lazyRouteComponent(
    () => import('@/pages/admin/AdminUserDetail'),
    'AdminUserDetailSkeleton',
  ),
})

const adminDslAgentRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/dsl-agent',
  head: () => ({
    meta: [{ title: formatTitle('DSL Agent') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminDslAgent')),
})

const adminCoachingSessionsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/coaching-sessions',
  head: () => ({
    meta: [{ title: formatTitle('Coaching Sessions') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminCoachingSessions')),
  pendingComponent: AdminCoachingSessionsPending,
})

const adminCoachingSessionDetailRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/coaching-sessions/$sessionId',
  head: () => ({
    meta: [{ title: formatTitle('Coaching Session') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminCoachingSessionDetail')),
  pendingComponent: lazyRouteComponent(
    () => import('@/pages/admin/AdminCoachingSessionDetail'),
    'AdminCoachingSessionDetailSkeleton',
  ),
})

const adminEscalationsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/escalations',
  head: () => ({
    meta: [{ title: formatTitle('Escalations') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminEscalations')),
  pendingComponent: AdminEscalationsPending,
})

const adminEscalationDetailRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/escalations/$escalationId',
  head: () => ({
    meta: [{ title: formatTitle('Escalation') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminEscalationDetail')),
  pendingComponent: lazyRouteComponent(
    () => import('@/pages/admin/AdminEscalationDetail'),
    'AdminEscalationDetailSkeleton',
  ),
})

const adminFounderBriefRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/users/$userId/business-brief',
  head: () => ({
    meta: [{ title: formatTitle('Founder Brief') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminFounderBrief')),
  pendingComponent: BusinessBriefPending,
})

const adminCategoriesRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/categories',
  head: () => ({
    meta: [{ title: formatTitle('Admin Categories') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminCategories')),
  pendingComponent: AdminCategoriesPending,
})

const adminCategoryDetailRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/categories/$categoryCode',
  head: () => ({
    meta: [{ title: formatTitle('Category Detail') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminCategoryDetail')),
  pendingComponent: AdminCategoryDetailPending,
})

const adminPlaybooksRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/playbooks',
  head: () => ({
    meta: [{ title: formatTitle('Admin Playbooks') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminPlaybooks')),
  pendingComponent: AdminPlaybooksPending,
})

const adminPlaybookDetailsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/playbooks/$slug',
  head: () => ({
    meta: [{ title: formatTitle('Edit Playbook') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminPlaybookDetails')),
  pendingComponent: PlaybookEditorSkeleton,
})

const adminPlaybookFounderPageRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/playbooks/$slug/founder-page',
  head: () => ({
    meta: [{ title: formatTitle('Playbook Founder Page') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminPlaybookFounderPage')),
  pendingComponent: PlaybookEditorSkeleton,
})

const adminPlaybookDoerPageRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/playbooks/$slug/doer-page',
  head: () => ({
    meta: [{ title: formatTitle('Playbook Doer Page') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminPlaybookDoerPage')),
  pendingComponent: PlaybookEditorSkeleton,
})

const adminPlaybookMediaRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/playbooks/$slug/media',
  head: () => ({
    meta: [{ title: formatTitle('Playbook Media') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminPlaybookMedia')),
  pendingComponent: PlaybookEditorSkeleton,
})

const adminPlaybookPublishRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/playbooks/$slug/publish',
  head: () => ({
    meta: [{ title: formatTitle('Publish Playbook') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminPlaybookPublish')),
  pendingComponent: PlaybookEditorSkeleton,
})

const adminSchemaRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/playbooks/$slug/schema',
  head: () => ({
    meta: [{ title: formatTitle('Playbook Schema') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminPlaybookSchema')),
  pendingComponent: AdminSchemaSkeleton,
})

const adminDoerSchemaRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/playbooks/$slug/doer-schema',
  head: () => ({
    meta: [{ title: formatTitle('Doer Schema') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminPlaybookDoerSchema')),
  pendingComponent: AdminSchemaSkeleton,
})

const adminFeedbackRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/feedback',
  head: () => ({
    meta: [{ title: formatTitle('Feedback Queue') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminFeedback')),
  validateSearch: validateAdminFeedbackSearch,
})

const adminFeedbackDetailRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/feedback/$feedbackId',
  head: () => ({
    meta: [{ title: formatTitle('Feedback Item') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminFeedbackDetail')),
  loader: ({ context: { queryClient, auth }, params: { feedbackId } }) => {
    if (!auth.isAuthenticated) return
    queryClient.ensureQueryData(adminFeedbackThreadOptions(feedbackId)).catch(() => {})
  },
})

const adminTeamsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/teams',
  head: () => ({
    meta: [{ title: formatTitle('Admin Teams') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminTeams')),
  pendingComponent: AdminTeamsPending,
})

const adminTeamDetailRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/teams/$teamId',
  head: () => ({
    meta: [{ title: formatTitle('Team Detail') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminTeamDetail')),
  pendingComponent: AdminTeamDetailPending,
})

const adminDoerGroupsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/doer-groups',
  head: () => ({
    meta: [{ title: formatTitle('Doer Groups') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminDoerGroups')),
  pendingComponent: AdminDoerGroupsPending,
})

const adminDoerGroupDetailRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/doer-groups/$groupId',
  head: () => ({
    meta: [{ title: formatTitle('Doer Group Detail') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminDoerGroupDetail')),
  pendingComponent: AdminDoerGroupDetailPending,
})

const adminGroupChatsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/group-chats',
  head: () => ({
    meta: [{ title: formatTitle('Group Chats') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminGroupChats')),
  pendingComponent: AdminGroupChatsPending,
})

const adminGroupChatDetailRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/group-chats/$groupChatId',
  head: () => ({
    meta: [{ title: formatTitle('Group Chat Detail') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminGroupChatDetail')),
  pendingComponent: AdminGroupChatDetailPending,
})

const adminFeatureFlagsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/feature-flags',
  head: () => ({
    meta: [{ title: formatTitle('Feature Flags') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminFeatureFlags')),
})

const adminFeatureFlagDetailRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/feature-flags/$flagKey',
  head: () => ({
    meta: [{ title: formatTitle('Feature Flag') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminFeatureFlagDetail')),
})

const adminCoachingCallsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/coaching-calls',
  head: () => ({
    meta: [{ title: formatTitle('Coaching Calls') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminCoachingCalls')),
})

const adminCoachingCallRecordingsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/coaching-calls/replays',
  head: () => ({
    meta: [{ title: formatTitle('Call Replays') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminCoachingCallRecordings')),
})

const adminMasterclassVaultRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/masterclass-vault',
  head: () => ({
    meta: [{ title: formatTitle('Masterclass Vault') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/admin/AdminMasterclassVault')),
})

// Masterclass editor tabs. Ordinary scrolling pages (no `fullHeight`), like the
// playbook editor — see the note on CmsEditorShell.
const adminMasterclassCourseDetailRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/masterclass-vault/$courseId',
  head: () => ({
    meta: [{ title: formatTitle('Edit Masterclass') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminMasterclassDetails')),
  pendingComponent: MasterclassEditorSkeleton,
})

const adminMasterclassLessonsRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/masterclass-vault/$courseId/lessons',
  head: () => ({
    meta: [{ title: formatTitle('Masterclass Lessons') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminMasterclassLessons')),
  pendingComponent: MasterclassEditorSkeleton,
})

const adminMasterclassPublishRoute = createRoute({
  getParentRoute: () => adminOnlyRoute,
  path: '/admin/masterclass-vault/$courseId/publish',
  head: () => ({
    meta: [{ title: formatTitle('Publish Masterclass') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/admin/AdminMasterclassPublish')),
  pendingComponent: MasterclassEditorSkeleton,
})

// ─── Escalation Officer routes ───────────────────────────────────
// The escalation officer's own surface, gated to the role and riding the shared
// shell. Scoped to the doers in the groups (pods) they own — distinct from the
// admin escalations queue (admin-only, sees everything).
const escalationOfficerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: '_escalationOfficer',
  beforeLoad: ({ context }) => requireRole(context, ['escalation_officer']),
  component: Outlet,
})

const escalationConsoleRoute = createRoute({
  getParentRoute: () => escalationOfficerRoute,
  path: '/escalations',
  head: () => ({
    meta: [{ title: formatTitle('Escalation Console') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/escalation/EscalationConsole')),
})

// The officer's investigation surface. Same screen as /admin/escalations/$id —
// the API scopes the record, so an officer only ever loads one in their pod or
// assigned to them.
const escalationDetailRoute = createRoute({
  getParentRoute: () => escalationOfficerRoute,
  path: '/escalations/$escalationId',
  head: () => ({
    meta: [{ title: formatTitle('Escalation') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/escalation/OfficerEscalationDetail')),
  pendingComponent: lazyRouteComponent(
    () => import('@/pages/escalation/OfficerEscalationDetail'),
    'OfficerEscalationDetailSkeleton',
  ),
})

// The coaching session that raised an escalation — origin context for the
// investigation. Two segments, so it never collides with $escalationId above.
const escalationSessionRoute = createRoute({
  getParentRoute: () => escalationOfficerRoute,
  path: '/escalations/sessions/$sessionId',
  head: () => ({
    meta: [{ title: formatTitle('Coaching Session') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/escalation/OfficerCoachingSessionDetail')),
  pendingComponent: lazyRouteComponent(
    () => import('@/pages/escalation/OfficerCoachingSessionDetail'),
    'OfficerCoachingSessionDetailSkeleton',
  ),
})

// A pod doer's full AI-coaching history. Two segments, like the session route
// above, so it never collides with $escalationId. The param is $userId (not
// $doerId) so PersonRef can hand the same `{ userId }` to this route or to the
// admin user page without branching on which one it is building.
const escalationDoerRoute = createRoute({
  getParentRoute: () => escalationOfficerRoute,
  path: '/escalations/doers/$userId',
  head: () => ({
    meta: [{ title: formatTitle('Coaching History') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/escalation/OfficerDoerSessions')),
  pendingComponent: lazyRouteComponent(
    () => import('@/pages/escalation/OfficerDoerSessions'),
    'OfficerDoerSessionsSkeleton',
  ),
})

// CX Risk Dashboard — visible to admin + escalation_officer (brief §15).
const cxRiskRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: '_cxRisk',
  beforeLoad: ({ context }) => requireRole(context, ['admin', 'escalation_officer']),
  component: Outlet,
})

const cxRiskDashboardRoute = createRoute({
  getParentRoute: () => cxRiskRoute,
  path: '/cx-risk',
  head: () => ({
    meta: [{ title: formatTitle('CX Risk Dashboard') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/cx-risk/CxRiskDashboard')),
})

// Weekly Review reviewer queue — admin + escalation_officer approve the amber/red
// recaps before the founder sees them. Lives outside /admin/* so officers (who
// aren't admins) can reach it, like /cx-risk above.
const weeklyReviewQueueGateRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: '_weeklyReviewQueue',
  beforeLoad: ({ context }) => requireRole(context, ['admin', 'escalation_officer']),
  component: Outlet,
})

const weeklyReviewQueueRoute = createRoute({
  getParentRoute: () => weeklyReviewQueueGateRoute,
  path: '/weekly-review-queue',
  head: () => ({
    meta: [{ title: formatTitle('Weekly Reviews') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/weekly-reviews/WeeklyReviewQueue')),
})

const weeklyReviewDetailRoute = createRoute({
  getParentRoute: () => weeklyReviewQueueGateRoute,
  path: '/weekly-review-queue/$reviewId',
  head: () => ({
    meta: [{ title: formatTitle('Weekly Review') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/weekly-reviews/WeeklyReviewDetail')),
  pendingComponent: lazyRouteComponent(
    () => import('@/pages/weekly-reviews/WeeklyReviewDetail'),
    'WeeklyReviewDetailSkeleton',
  ),
})

// ─── Dashboard routes (the four non-admin roles) ─────────────────
// Pass-through guard wrapper under the shared appLayoutRoute. The layout + the
// restricted-doer rule are handled by appLayoutRoute; this only narrows access
// to the non-admin dashboard roles for the pages below.
const dashboardRolesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: '_dashboardRoles',
  beforeLoad: ({ context }) => requireRole(context, ['founder', 'doer', 'doing_team', 'intake_team']),
  component: Outlet,
})

// Notifications — every role that can receive one, which is every role. Parented
// to the app layout rather than dashboardRolesRoute (founder/doer/doing_team/
// intake_team): admins and escalation officers get notifications too, and gating
// the page they land on made those notifications undiscoverable.
const notificationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/notifications',
  head: () => ({
    meta: [{ title: formatTitle('Notifications') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/Notifications')),
  pendingComponent: NotificationsPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    // Prefetch first page — the page itself uses useInfiniteQuery
    await queryClient
      .prefetchInfiniteQuery({
        queryKey: ['notifications', 'list'],
        queryFn: () => notificationService.fetchNotifications(20, 0),
        initialPageParam: 0,
      })
      .catch(() => {})
  },
})

// Device notifications (web push subscribe / install to home screen)
const deviceNotificationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/notifications/devices',
  head: () => ({
    meta: [{ title: formatTitle('Device Notifications') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/DeviceNotifications')),
})

// Founder-only routes
const founderOnlyRoute = createRoute({
  getParentRoute: () => dashboardRolesRoute,
  id: '_founderOnly',
  beforeLoad: ({ context }) => requireRole(context, ['founder']),
  component: Outlet,
})

// Onboarding step 2 — the Business Brain intake conversation. Founder-only: it
// runs against the caller's own onboarding session, and a doer has none.
// Full-height because it is an immersive chat surface with its own header, not
// a PageLayout page (see FounderIntake.module.css).
const founderIntakeRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/founder-intake',
  head: () => ({
    meta: [{ title: formatTitle('Business Brain intake') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/FounderIntake')),
})

const startHereRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/start-here',
  head: () => ({
    meta: [{ title: formatTitle('Start Here') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/StartHere')),
  pendingComponent: StartHerePending,
})

// Founder + Doer routes
const founderDoerRoute = createRoute({
  getParentRoute: () => dashboardRolesRoute,
  id: '_founderDoer',
  beforeLoad: ({ context }) => requireRole(context, ['founder', 'doer']),
  component: Outlet,
})

const founderPlaybookAccessRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  id: '_founderPlaybookAccess',
  component: PlaybookAccessLayout,
  errorComponent: PlaybookAccessErrorBoundary,
})

const doingBlueprintRoute = createRoute({
  getParentRoute: () => founderPlaybookAccessRoute,
  path: '/doing-blueprint',
  head: () => ({
    meta: [{ title: formatTitle('Doing Blueprint') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/DoingBlueprint')),
  pendingComponent: DoingBlueprintPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await ensureWorkspaceAccess(queryClient)
    await Promise.all([
      queryClient.ensureQueryData(blueprintStepsOptions()),
      queryClient.ensureQueryData(masterPlaybooksOptions()),
    ]).catch(() => {})
  },
})

const playbooksRoute = createRoute({
  getParentRoute: () => founderPlaybookAccessRoute,
  path: '/playbooks',
  head: () => ({
    meta: [{ title: formatTitle('Playbooks') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/Playbooks')),
  pendingComponent: PlaybooksPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await ensureWorkspaceAccess(queryClient)
    await queryClient.ensureQueryData(blueprintStepsOptions())
  },
})

/**
 * The parent of `/playbooks/$slug` alone — founders, doers, and admins.
 *
 * Admins are here because the CMS editor's "View live" button points at this
 * route and an admin is the only role that ever clicks it; under
 * `founderDoerRoute` with its siblings it bounced them to `/admin/stats`
 * instead, which is what ClickUp 86d46fzdu reported. Parented to
 * `appLayoutRoute` rather than `dashboardRolesRoute` for the same reason
 * `/notifications` is: admin is not a dashboard role, and must not pick up the
 * rest of that subtree just to read one page.
 *
 * An admin gets the page read-only. Every affordance on it is already gated on
 * `founder`/`doer`, and the workspace read it derives playbook status from
 * answers 403 for an admin — so the loader and the page both skip it, and the
 * page falls through to its no-workspace-status branch: the content, which is
 * exactly what "view live" means for the person who just authored it.
 *
 * Same layout and error boundary as the founder subtree; both already no-op for
 * anyone who is not a founder.
 */
const playbookDetailAccessRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: '_playbookDetailAccess',
  beforeLoad: ({ context }) => requireRole(context, ['founder', 'doer', 'admin']),
  component: PlaybookAccessLayout,
  errorComponent: PlaybookAccessErrorBoundary,
})

export const playbookDetailRoute = createRoute({
  getParentRoute: () => playbookDetailAccessRoute,
  path: '/playbooks/$slug',
  head: () => ({
    meta: [{ title: formatTitle('Playbook') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/PlaybookDetail')),
  pendingComponent: PlaybookDetailPending,
  loader: async ({ context: { queryClient, auth }, params: { slug } }) => {
    if (!auth.isAuthenticated) return
    // An admin previewing from the CMS has no workspace of their own, and
    // `GET /workspace` answers them 403 — asking would turn the page into
    // its error state. Content only.
    const workspace = auth.profile?.role === 'admin' ? null : await ensureWorkspaceAccess(queryClient)
    await Promise.allSettled([
      queryClient.ensureQueryData(playbookDetailOptions(slug)),
      queryClient.ensureQueryData(blueprintStepsOptions()),
    ])
    if (!workspace) return
    const wp = workspace.playbooks.find((p) => p.playbook?.slug === slug)
    if (wp?.playbook_brief_id) {
      await queryClient.ensureQueryData(playbookBriefOptions(wp.playbook_brief_id)).catch(() => {})
    }
  },
})

const businessBriefRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/business-brief',
  head: () => ({
    meta: [{ title: formatTitle('Business Brief') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/BusinessBrief')),
  pendingComponent: BusinessBriefPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(businessBriefOptions()).catch(() => {})
  },
})

/**
 * Onboarding step 10's launch checklist (ClickUp `86d3ukp27`).
 *
 * Under `founderDoerRoute`, like `/business-brief`, because the API's read is
 * deliberately wider than its write: the founder fills the checklist in and
 * their assigned doer reads it — before launch day, which is the point of
 * collecting it at all. The page renders read-only for anyone but the founder.
 *
 * Deliberately NOT in `FLAGGED_ROUTE_PREFIXES`. `founder-onboarding` is a
 * per-user rollout flag; gating this route on it would bounce the doer, who does
 * not carry the founder's flag but is half the audience.
 */
const launchChecklistRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/launch-checklist',
  head: () => ({
    meta: [{ title: formatTitle('Launch Checklist') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/LaunchChecklist')),
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(launchChecklistOptions()).catch(() => {})
  },
})

/**
 * Onboarding step 10's surface — Launch Day (ClickUp `86d40tuc8`).
 *
 * Founder-only, unlike the `/launch-checklist` page above: a doer has no launch
 * day of their own to count down to, and nothing on this page is theirs to read.
 * Which is exactly what lets it sit in `FLAGGED_ROUTE_PREFIXES` behind
 * `founder-onboarding` when the checklist cannot — there is no second audience to
 * bounce, and outside the rollout the page could only say "you have no launch
 * day", since a founder with no onboarding run has no step 10 either.
 */
const launchDayRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/launch-day',
  head: () => ({
    meta: [{ title: formatTitle('Launch Day') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/LaunchDay')),
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(launchDayOptions()).catch(() => {})
  },
})

/**
 * Onboarding step 9's surface — the Doer profile.
 *
 * Two audiences on one route, the `/launch-checklist` shape: the doer edits
 * their own profile, their founder reads it and ticks the watch confirmation.
 * The API enforces that split (the GET serves both, the PATCH is doer-only), so
 * the page branches on role rather than growing a second surface — and step 9's
 * CTA gets a static route it can link to.
 *
 * Deliberately NOT in `FLAGGED_ROUTE_PREFIXES`, for the same reason as the
 * launch checklist: `founder-onboarding` is a per-user rollout flag, and gating
 * this route on it would bounce the doer, who never carries their founder's
 * flag but owns half the page.
 */
const doerProfileRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/doer-profile',
  head: () => ({
    meta: [{ title: formatTitle('Doer Profile') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/DoerProfile')),
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    // Only the half this role renders — the other request answers a different
    // question, and for a founder it would 403.
    if (auth.profile?.role === 'doer') {
      await queryClient.ensureQueryData(doerProfileOptions()).catch(() => {})
      return
    }
    await queryClient.ensureQueryData(workspaceDoerProfilesOptions()).catch(() => {})
  },
})

const filesRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/files',
  head: () => ({
    meta: [{ title: formatTitle('Files') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/Files')),
  pendingComponent: FilesPending,
})

const fileFolderRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/files/$folderId',
  head: () => ({
    meta: [{ title: formatTitle('Files') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/Files')),
  pendingComponent: FilesPending,
})

// Shared shell: persistent left rail + column grid. Children only render the
// feed (the <Outlet />), so navigating between a group / mentions / replies
// never tears down the sidebar. The list data is prefetched here once.
// Groups is cross-role: founders and admins use the same view at /groups
// (admins browse every group — scope is derived from the role, not the path).
// Its guard admits admins on top of founders; the layout + restricted-doer rule
// come from the shared appLayoutRoute above. Doers are excluded here rather than
// only losing the nav entry (ClickUp 86d3ve54a), so a bookmarked or typed URL
// bounces them home instead of opening a surface they are no longer offered.
const groupsLayoutRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: '_groupsLayout',
  beforeLoad: ({ context }) => requireRole(context, ['founder', 'admin']),
  component: GroupsLayout,
  loader: ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    // Non-blocking — the sidebar renders its own skeleton while these resolve,
    // so the shell never gates the feed behind the group lists.
    queryClient.ensureQueryData(groupsListOptions('mine')).catch(() => {})
    queryClient.ensureQueryData(groupsListOptions('discover')).catch(() => {})
  },
})

const groupsRoute = createRoute({
  getParentRoute: () => groupsLayoutRoute,
  path: '/groups',
  head: () => ({
    meta: [{ title: formatTitle('Groups') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/Groups')),
  pendingComponent: GroupsPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(groupsListOptions('mine')).catch(() => {})
  },
})

const groupDetailRoute = createRoute({
  getParentRoute: () => groupsLayoutRoute,
  path: '/groups/$groupId',
  head: () => ({
    meta: [{ title: formatTitle('Community') }],
  }),
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === 'string' && search.q ? { q: search.q } : {},
  component: lazyRouteComponent(() => import('@/pages/GroupDetail')),
  pendingComponent: GroupDetailPending,
  loader: async ({ context: { queryClient, auth }, params: { groupId } }) => {
    if (!auth.isAuthenticated) return
    await Promise.all([
      queryClient.ensureQueryData(groupDetailOptions(groupId)).catch(() => {}),
      queryClient.ensureQueryData(groupMembersOptions(groupId)).catch(() => {}),
      queryClient.ensureInfiniteQueryData(groupPostsInfiniteOptions(groupId)).catch(() => {}),
    ])
  },
})

const groupMentionsRoute = createRoute({
  getParentRoute: () => groupsLayoutRoute,
  path: '/groups/mentions',
  head: () => ({
    meta: [{ title: formatTitle('Mentions') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/GroupMentions')),
  pendingComponent: GroupInboxPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(userMentionsOptions()).catch(() => {})
  },
})

const groupRepliesRoute = createRoute({
  getParentRoute: () => groupsLayoutRoute,
  path: '/groups/replies',
  head: () => ({
    meta: [{ title: formatTitle('Replies') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/GroupReplies')),
  pendingComponent: GroupInboxPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(userRepliesOptions()).catch(() => {})
  },
})

// Admin groups — the exact same unified groups view (GroupsLayout + the same
// ─── Workflows tree (gated by `workflows` feature flag via useFeatureFlagRouteGuard) ──
const workflowsRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/workflows',
  head: () => ({
    meta: [{ title: formatTitle('Workflows') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/workflows/Workflows')),
})

const workflowSmcTriggerRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/workflows/social-media-calendar',
  head: () => ({
    meta: [{ title: formatTitle('Social Media Calendar — New Run') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/workflows/social-media-calendar/Trigger')),
})

const workflowSmcRunsListRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/workflows/social-media-calendar/runs',
  head: () => ({
    meta: [{ title: formatTitle('Social Media Calendar — Runs') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/workflows/social-media-calendar/RunsList')),
})

const workflowSmcRunStatusRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/workflows/social-media-calendar/runs/$wpId',
  head: () => ({
    meta: [{ title: formatTitle('Social Media Calendar — Run') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/workflows/social-media-calendar/RunStatus')),
})

const workflowSmcReviewRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/workflows/social-media-calendar/review/$wpId',
  head: () => ({
    meta: [{ title: formatTitle('Social Media Calendar — Review') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/workflows/social-media-calendar/Review')),
})

const coachingCallsRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/coaching-calls',
  head: () => ({
    meta: [{ title: formatTitle('Coaching Calls') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/CoachingCalls')),
  pendingComponent: CoachingCallsPending,
  // Blocking, so the calendar arrives populated instead of painting an empty
  // month grid and popping. `defaultPreload: 'intent'` runs this on nav-link
  // hover, so the click itself usually resolves from cache; when it doesn't,
  // CoachingCallsPending covers the wait with a calendar skeleton.
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    // Swallowed: a failed prefetch must not block the page — the hero and
    // recordings still render and the page's own useQuery surfaces the error.
    await queryClient.ensureQueryData(coachingCallsListOptions(buildCoachingCallsWindow())).catch(() => {})
  },
})

const coachingCallReplayRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/coaching-calls/replays/$id',
  head: () => ({
    meta: [{ title: formatTitle('Coaching Call Replay') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/CoachingCallReplay')),
  pendingComponent: CoachingCallReplayPending,
  staticData: { fullHeight: true },
})

const doersUniversityRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/doers-university',
  head: () => ({
    meta: [{ title: formatTitle("Doer's University") }],
  }),
  component: lazyRouteComponent(() => import('@/pages/DoersUniversity')),
  pendingComponent: DoersUniversityPending,
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(doersUniversityCatalogOptions()),
})

const masterclassVaultRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/masterclass-vault',
  head: () => ({
    meta: [{ title: formatTitle('Masterclass Vault') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/MasterclassVault')),
  pendingComponent: MasterclassVaultPending,
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(masterclassVaultCatalogOptions()),
})

/**
 * The parent of `/masterclass-vault/$courseSlug` alone — founders and admins.
 *
 * The masterclass editor's "View live" points here, and the same reasoning as
 * `_playbookDetailAccess` applies: an admin is the only role that clicks it, and
 * a founder-only guard sends them to `/admin/stats` instead (ClickUp 86d46fzdu).
 * A separate route rather than one shared with the playbook one because the
 * founder-facing audiences genuinely differ — a doer has a playbook page to
 * read and no masterclass.
 *
 * The role guard is not the only one here: `masterclass-vault` is also a rollout
 * flag, and `useFeatureFlagRouteGuard` bounces on it independently — see
 * `ADMIN_UNGATED_FLAGS`.
 *
 * Read-only for an admin: the page records a `content_view` on mount, and an
 * admin looking at their own work is not a founder studying it, so it does not
 * fire. Everything else on the page is course content, which is the point.
 */
const masterclassLessonAccessRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  id: '_masterclassLessonAccess',
  beforeLoad: ({ context }) => requireRole(context, ['founder', 'admin']),
  component: Outlet,
})

const masterclassLessonRoute = createRoute({
  getParentRoute: () => masterclassLessonAccessRoute,
  path: '/masterclass-vault/$courseSlug',
  head: () => ({
    meta: [{ title: formatTitle('Masterclass Vault') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/MasterclassLesson')),
  pendingComponent: MasterclassLessonPending,
  staticData: { fullHeight: true },
  loader: ({ context: { queryClient }, params: { courseSlug } }) =>
    queryClient.ensureQueryData(masterclassVaultCourseOptions(courseSlug)),
})

const foundationsRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/foundations',
  head: () => ({
    meta: [{ title: formatTitle('Founder Foundations') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/Foundations')),
  pendingComponent: FoundationsPending,
  // Deliberately NOT fullHeight. The course used to be a lesson player that
  // owned its own scroll; it is a grid of cards in the page's flow now, so it
  // needs the shell's ScrollArea like every other PageLayout page. Setting the
  // flag here takes that ScrollArea away (see DashboardLayout) and the grid
  // becomes unreachable below the fold.
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(foundationsCourseOptions()),
})

const foundersLoungeRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/founders-lounge',
  head: () => ({
    meta: [{ title: formatTitle("Founder's Lounge") }],
  }),
  component: lazyRouteComponent(() => import('@/pages/FoundersLounge')),
  pendingComponent: FoundersLoungePending,
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(foundersLoungeCatalogOptions()),
})

const foundersLoungeArticleRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/founders-lounge/$articleSlug',
  head: () => ({
    meta: [{ title: formatTitle("Founder's Lounge") }],
  }),
  component: lazyRouteComponent(() => import('@/pages/FoundersLoungeArticle')),
  pendingComponent: FoundersLoungeArticlePending,
  loader: ({ context: { queryClient }, params: { articleSlug } }) =>
    queryClient.ensureQueryData(foundersLoungeArticleOptions(articleSlug)),
})

const techStackFinderRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/tech-stack-finder',
  head: () => ({
    meta: [{ title: formatTitle('Tech Stack Finder') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/TechStackFinder')),
  pendingComponent: TechStackFinderPending,
})

const kickOffCallRoute = createRoute({
  getParentRoute: () => founderOnlyRoute,
  path: '/kick-off-call',
  head: () => ({
    meta: [{ title: formatTitle('Kick-Off Call') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/KickOffCall')),
  pendingComponent: KickOffCallPending,
})

// Status groups the List view renders, in display order. Kept in sync with
// TASK_GROUPS in TasksGroupedList.tsx (duplicated here rather than imported so
// the lazy Tasks chunk isn't pulled into the router bundle).
const TASKS_LIST_STATUSES: TaskStatus[] = ['in_progress', 'blocked', 'not_started', 'completed', 'cancelled']
// The Kanban columns, from COLUMNS in TasksKanbanBoard.tsx. Fewer statuses than
// the list, and ordered by priority server-side — a *different query key*, so
// warming the list's sections warms nothing this board reads.
const TASKS_KANBAN_STATUSES: TaskStatus[] = ['not_started', 'in_progress', 'blocked', 'completed']
// The calendar wants the whole visible range rather than a page. Kept in sync
// with TasksCalendarTab in pages/Tasks.tsx.
const TASKS_CALENDAR_LIMIT = 500

/** The filters the arriving board will render with, from the URL it arrives at. */
function tasksBoardFiltersFor(
  queryClient: QueryClient,
  auth: AuthContextType,
  search: TasksSearch,
  projectSlug: string | undefined,
) {
  // The project is a path segment, and it's a *slug*, so it has to be resolved
  // against the project list before the filters can be built. Awaited rather
  // than fired off: every prefetch has to carry `list_id` or it warms the wrong
  // cache key, which is invisible (the board just refetches).
  return queryClient
    .ensureQueryData(labelListOptions('list'))
    .catch(() => [])
    .then((projects) =>
      // `taskBoardFilters` is the one derivation the loaders and the page share,
      // and that identity is what makes the warmed cache hit — including on a
      // back-nav or a shared link onto an already-filtered board.
      taskBoardFilters(
        search,
        useDashboardStore.getState().taskScope,
        auth.profile?.role !== 'founder',
        resolveProjectListId(projects, projectSlug),
      ),
    )
}

/**
 * What the loaders below need out of their route context. Taken as plain
 * arguments rather than as an annotated loader signature: annotating the
 * context object lets TypeScript infer the *route's* params from it, which
 * silently costs `/tasks/calendar/$date` its `date`.
 */
type TasksLoaderCause = 'enter' | 'stay' | 'preload'

/**
 * Warming that every view needs, whichever one is arriving: the two counts the
 * empty states are decided by, the roster names resolve through, and the two
 * label reads the switcher and the collection filter render.
 *
 * Only an *arrival* is awaited. A tab switch or a filter change is a `'stay'` on
 * this same match, and awaiting there holds the URL commit — and with it the
 * whole board — behind a round trip on top of the debounce, with nothing on
 * screen saying so; the board just sits on its previous rows. Every view has
 * its own per-section loading state, which is what the rest of the app does
 * with a filtered list. The prefetches still fire, so the view's own queries
 * find them in flight.
 */
async function warmTasksShared(
  { queryClient, auth }: RouterContext,
  search: TasksSearch,
  projectSlug: string | undefined,
  cause: TasksLoaderCause,
) {
  if (!auth.isAuthenticated) return
  const filters = await tasksBoardFiltersFor(queryClient, auth, search, projectSlug)
  const warm = [
    // Workspace task count drives the "no tasks yet" state.
    queryClient.ensureQueryData(taskListOptions({ limit: 1 })).catch(() => {}),
    // Current filter count drives the scoped/no-results empty states.
    queryClient.ensureQueryData(taskListOptions({ ...filters, limit: 1 })).catch(() => {}),
    queryClient.ensureQueryData(contactsOptions()).catch(() => {}),
    // The collection filter's options.
    queryClient.ensureQueryData(labelListOptions('collection')).catch(() => {}),
    // The project switcher shows a count per project, from one aggregate.
    queryClient.ensureQueryData(labelCountsOptions('list')).catch(() => {}),
  ]
  if (cause === 'enter') await Promise.all(warm)
}

/**
 * The queries one tab of the board actually reads.
 *
 * Per view rather than one prefetch on the parent, because the four views share
 * almost no query keys — Kanban orders its columns by priority, the calendar
 * asks for a range-sized page, Collections reads counts — so a parent warming
 * one shape blocks the arrival at all four while warming the right keys for
 * one. Each tab route pairs this with `staticData: { tasksTab }`, which is also
 * how the page knows which tab is showing: the route says what it is, rather
 * than the page re-deriving it from the pathname.
 *
 * Awaited on arrival only, for the reason on `warmTasksShared`.
 */
async function warmTasksView(
  tasksTab: TasksTab,
  { queryClient, auth }: RouterContext,
  search: TasksSearch,
  projectSlug: string | undefined,
  cause: TasksLoaderCause,
) {
  if (!auth.isAuthenticated) return
  const filters = await tasksBoardFiltersFor(queryClient, auth, search, projectSlug)
  // Per-status keys, exactly as the sections and columns build them: a section
  // that names its status drops `excludeCompleted`, and the completed one is
  // not rendered at all while it is on. Warming the other shape warms keys
  // nothing reads — invisibly, since the board just refetches.
  const byStatus = statusQueryFilters(filters)
  const infinite = (extra: Parameters<typeof taskListInfiniteOptions>[0]) =>
    queryClient.ensureInfiniteQueryData(taskListInfiniteOptions({ ...byStatus, ...extra })).catch(() => {})

  let warm: Promise<unknown>[] = []
  if (tasksTab === 'list') {
    // Grouped by project the sections are `list_id` queries whose ids come out
    // of the project list, so there is nothing to warm until that resolves —
    // and the status keys below are not what that mode reads. The sections
    // carry their own loading state; better nothing than the wrong keys.
    warm =
      search.group === 'project' && !projectSlug
        ? []
        : boardStatuses(TASKS_LIST_STATUSES, filters).map((status) => infinite({ status }))
  } else if (tasksTab === 'kanban') {
    warm = boardStatuses(TASKS_KANBAN_STATUSES, filters).map((status) =>
      infinite({ status, order: 'priority' }),
    )
  } else if (tasksTab === 'calendar') {
    warm = [
      queryClient
        .ensureQueryData(taskListOptions({ ...filters, limit: TASKS_CALENDAR_LIMIT }))
        .catch(() => {}),
    ]
  } else {
    // The grid is one aggregate — the collections *with* their task counts, not
    // the plain list the filter dropdown reads.
    warm = [queryClient.ensureQueryData(labelCountsOptions('collection')).catch(() => {})]
  }
  if (cause === 'enter') await Promise.all(warm)
}

const tasksRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/tasks',
  head: () => ({
    meta: [{ title: formatTitle('Tasks') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/Tasks')),
  pendingComponent: TasksPending,
  // Inherited by the tab children, so /tasks/kanban?q=… and
  // /tasks/calendar/2026-07-21?q=… validate the same way.
  validateSearch: validateTasksSearch,
  // Read off `location` rather than declaring `loaderDeps`. Deps would give each
  // filter set its own match id, and a fresh match is one the router has no data
  // for — so every debounced keystroke would drop the board for the pending
  // skeleton. Without them the match is reused and the loader re-runs against
  // the new URL, which is what `cause` is then used to keep cheap.
  loader: ({ context, location, params, cause }) =>
    warmTasksShared(context, location.search, (params as { projectSlug?: string }).projectSlug, cause),
})

const tasksIndexRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/tasks/list', replace: true })
  },
})

const tasksListRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'list',
  component: () => null,
  staticData: { tasksTab: 'list' },
  loader: ({ context, location, cause }) => warmTasksView('list', context, location.search, undefined, cause),
})

const tasksKanbanRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'kanban',
  component: () => null,
  staticData: { tasksTab: 'kanban' },
  loader: ({ context, location, cause }) =>
    warmTasksView('kanban', context, location.search, undefined, cause),
})

// The Collections tab. A static segment, so it outranks the `/tasks/$taskId`
// sibling the same way `/tasks/new` does.
const tasksCollectionsRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'collections',
  component: () => null,
  staticData: { tasksTab: 'collections' },
  loader: ({ context, location, cause }) =>
    warmTasksView('collections', context, location.search, undefined, cause),
})

const tasksCalendarRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'calendar',
  component: () => null,
  staticData: { tasksTab: 'calendar' },
  loader: ({ context, location, cause }) =>
    warmTasksView('calendar', context, location.search, undefined, cause),
})

const tasksCalendarDateRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'calendar/$date',
  component: () => null,
  staticData: { tasksTab: 'calendar' },
  loader: ({ context, location, cause }) =>
    warmTasksView('calendar', context, location.search, undefined, cause),
})

// ─── The same three views, scoped to one project ────────────────
//
// A project is `kind = 'list'` — exactly one per task — so it partitions the
// board, which is what lets it be a *scope* rather than a filter. That's why it
// is a path segment: it's the entity the board is about, and every view exists
// inside it (`/tasks/project/marketing/kanban`) as well as across all of them.
//
// The literal `project` segment is what keeps this unambiguous. Without it, a
// project a founder named "Calendar" would produce `/tasks/calendar/list`, which
// the `calendar/$date` route above would claim with `date = 'list'`.
const tasksProjectListRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'project/$projectSlug/list',
  component: () => null,
  staticData: { tasksTab: 'list' },
  loader: ({ context, location, params, cause }) =>
    warmTasksView('list', context, location.search, params.projectSlug, cause),
})

const tasksProjectKanbanRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'project/$projectSlug/kanban',
  component: () => null,
  staticData: { tasksTab: 'kanban' },
  loader: ({ context, location, params, cause }) =>
    warmTasksView('kanban', context, location.search, params.projectSlug, cause),
})

const tasksProjectCalendarRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'project/$projectSlug/calendar',
  component: () => null,
  staticData: { tasksTab: 'calendar' },
  loader: ({ context, location, params, cause }) =>
    warmTasksView('calendar', context, location.search, params.projectSlug, cause),
})

const tasksProjectCalendarDateRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'project/$projectSlug/calendar/$date',
  component: () => null,
  staticData: { tasksTab: 'calendar' },
  loader: ({ context, location, params, cause }) =>
    warmTasksView('calendar', context, location.search, params.projectSlug, cause),
})

// `/tasks/project/marketing` on its own is a project with no view chosen; send
// it to the List view rather than 404ing a link someone shortened by hand.
const tasksProjectIndexRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'project/$projectSlug',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/tasks/project/$projectSlug/list',
      params: { projectSlug: params.projectSlug },
      replace: true,
    })
  },
})

// The Collections tab used to be "Projects", before `kind = 'list'` took that
// word. Keep the old addresses working — they were shipped, so they're in
// history and in anything anyone pasted into Slack.
const tasksProjectsLegacyRoute = createRoute({
  getParentRoute: () => tasksRoute,
  path: 'projects',
  beforeLoad: () => {
    throw redirect({ to: '/tasks/collections', replace: true })
  },
})

// Sibling of /tasks rather than a child: the Tasks page renders no Outlet, so a
// child route would paint the board instead of this page. The static `new`
// segment outranks `$taskId`, so /tasks/new never resolves as a task detail.
const taskCreateRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/tasks/new',
  head: () => ({
    meta: [{ title: formatTitle('Create Task') }],
  }),
  // Prefills the due date for the calendar's per-day "+" button. The page has no
  // return-path param — it pops the entry it was pushed onto instead.
  // `deadline` prefills from the calendar's per-day "+". `collection` and `list`
  // prefill the containers when the form is opened from inside one, so a task
  // created there actually lands there. (`list` is the project — "Project" is UI
  // copy over `kind = 'list'`.)
  validateSearch: (
    search: Record<string, unknown>,
  ): { deadline?: string; collection?: string; list?: string } => {
    const result: { deadline?: string; collection?: string; list?: string } = {}
    if (typeof search.deadline === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search.deadline)) {
      result.deadline = search.deadline
    }
    if (typeof search.collection === 'string' && search.collection) {
      result.collection = search.collection
    }
    if (typeof search.list === 'string' && search.list) {
      result.list = search.list
    }
    return result
  },
  component: lazyRouteComponent(() => import('@/pages/TaskCreate')),
  pendingComponent: TaskCreatePending,
  loader: ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    // Warm the workspace roster the assignee picker needs, without awaiting.
    // Every entry point is a button, not a Link, so `defaultPreload: 'intent'`
    // never warms this route — awaiting would put a cold `?detailed=true` round
    // trip between the click and the form, where the dialog opened instantly.
    // `useAssignableMembers` offers "me" until the roster lands.
    queryClient.ensureQueryData(workspaceDetailedOptions()).catch(() => {})
  },
})

// A collection is a page of its own, not a tab panel: it has its own title, back
// affordance and grouping. Sibling of /tasks for the same reason /tasks/new is
// — the Tasks page renders no Outlet, so a child route would paint the board.
// Three segments, so it can never collide with /tasks/$taskId.
const taskCollectionDetailRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/tasks/collections/$collectionId',
  head: () => ({
    meta: [{ title: formatTitle('Collection') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/TaskCollectionDetail')),
  pendingComponent: TaskCollectionDetailPending,
  loader: async ({ params, context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    // The page resolves its collection's name from the collection list, and its
    // sections are the projects used *inside* this collection — which is the
    // scoped counts query, not the workspace-wide project query. Warm the exact
    // two the page reads: a near-miss here is invisible (the page just refetches
    // and the waterfall this loader exists to remove comes back).
    await Promise.all([
      queryClient.ensureQueryData(labelListOptions('collection')).catch(() => {}),
      queryClient.ensureQueryData(labelCountsOptions('list', params.collectionId)).catch(() => {}),
    ])
  },
})

// The old address for the page above, from when a collection was called a
// project. Kept so shipped links keep resolving.
const taskProjectDetailLegacyRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/tasks/projects/$collectionId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/tasks/collections/$collectionId',
      params: { collectionId: params.collectionId },
      replace: true,
    })
  },
})

const taskDetailRoute = createRoute({
  getParentRoute: () => founderDoerRoute,
  path: '/tasks/$taskId',
  head: () => ({
    meta: [{ title: formatTitle('Task') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/TaskDetail')),
  pendingComponent: TaskDetailPending,
  loader: async ({ context: { queryClient, auth }, params: { taskId } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(taskDetailOptions(taskId)).catch(() => {})
  },
})

// Doer routes
const doerOnlyRoute = createRoute({
  getParentRoute: () => dashboardRolesRoute,
  id: '_doerOnly',
  beforeLoad: ({ context }) => requireRole(context, ['doer']),
  component: Outlet,
})

// Pre-session prep is no longer its own page — it's the no-session state of the
// single /ai-coaching route. Keep the path as a permanent redirect so existing
// links and bookmarks still land in the right place.
const preSessionPrepRoute = createRoute({
  getParentRoute: () => doerOnlyRoute,
  path: '/pre-session-prep',
  beforeLoad: () => {
    throw redirect({ to: '/ai-coaching' })
  },
})

const doerHomeRoute = createRoute({
  getParentRoute: () => doerOnlyRoute,
  path: '/doer-home',
  head: () => ({
    meta: [{ title: formatTitle('Doer Home') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/DoerHome')),
  pendingComponent: DoerHomePending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    // Warm every query the home tiles read so the page paints complete in one
    // pass — without the task/contacts prefetch, TasksTile and ChatThreadsTile
    // mount with their own inner skeletons and flash content in a second wave.
    await Promise.all([
      queryClient.ensureQueryData(workspaceDetailedOptions()).catch(() => {}),
      queryClient.ensureQueryData(meFoundersOptions(auth.profile?.id)).catch(() => {}),
      queryClient.ensureQueryData(meLoyaltyOptions(auth.profile?.id)).catch(() => {}),
      queryClient.ensureQueryData(taskListOptions({ mine: true })).catch(() => {}),
      queryClient.ensureQueryData(contactsOptions()).catch(() => {}),
    ])
  },
})

const aiCoachingRoute = createRoute({
  getParentRoute: () => doerOnlyRoute,
  path: '/ai-coaching',
  // The single AI-coaching route: renders the pre-session prep form until the
  // doer has an in-progress session, then the coaching chat. The active session
  // is resolved server-side (see AiCoaching.tsx) — there's no session id in the
  // URL, so the route carries no search params.
  head: () => ({
    meta: [{ title: formatTitle('AI Coaching') }],
  }),
  staticData: { fullHeight: true },
  component: lazyRouteComponent(() => import('@/pages/AiCoaching')),
})

// Independent "submit your work" form (ClickUp 86d3g825y). Doer-only and gated
// by the same `pre-session-prep` rollout flag as AI Coaching (including direct
// visits via useFeatureFlagRouteGuard).
const submitWorkRoute = createRoute({
  getParentRoute: () => doerOnlyRoute,
  path: '/submit-work',
  head: () => ({
    meta: [{ title: formatTitle('Submit Work') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/SubmitWork')),
})

// Doer loyalty balance + history. Doer-only, and gated by its own
// `loyalty-points` flag rather than the coaching rollout that earns the points
// (including direct visits via useFeatureFlagRouteGuard) — the display is Chunk
// 1 only, so coaching must be able to launch without it.
const loyaltyRoute = createRoute({
  getParentRoute: () => doerOnlyRoute,
  path: '/loyalty',
  head: () => ({
    meta: [{ title: formatTitle('Loyalty points') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/Loyalty')),
})

const coachingSessionHistoryRoute = createRoute({
  getParentRoute: () => doerOnlyRoute,
  path: '/coaching-sessions',
  head: () => ({
    meta: [{ title: formatTitle('Coaching History') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/CoachingSessionHistory')),
})

const coachingSessionDetailRoute = createRoute({
  getParentRoute: () => doerOnlyRoute,
  path: '/coaching-sessions/$sessionId',
  head: () => ({
    meta: [{ title: formatTitle('Coaching Session') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/CoachingSessionDetail')),
})

const teamsRoute = createRoute({
  getParentRoute: () => doerOnlyRoute,
  path: '/teams',
  head: () => ({
    meta: [{ title: formatTitle('Teams') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/Teams')),
  pendingComponent: TeamsPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await Promise.all([
      queryClient.ensureQueryData(workspaceDetailedOptions()),
      queryClient.ensureQueryData(blueprintStepsOptions()),
      queryClient.ensureQueryData(assignedProjectsDetailedOptions()),
    ]).catch(() => {})
  },
})

// Doing Team routes
const doingTeamRoute = createRoute({
  getParentRoute: () => dashboardRolesRoute,
  id: '_doingTeam',
  beforeLoad: ({ context }) => requireRole(context, ['doing_team']),
  component: Outlet,
})

const projectsRoute = createRoute({
  getParentRoute: () => doingTeamRoute,
  path: '/projects',
  head: () => ({
    meta: [{ title: formatTitle('Projects') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/TeamMemberDashboard')),
  pendingComponent: MyTeamProjectsPending,
})

const projectDetailRoute = createRoute({
  getParentRoute: () => doingTeamRoute,
  path: '/projects/$projectId',
  head: () => ({
    meta: [{ title: formatTitle('Project') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/ProjectDetail')),
  pendingComponent: MyTeamProjectsPending,
})

const legacyMyTeamProjectsRoute = createRoute({
  getParentRoute: () => doingTeamRoute,
  path: '/my-team-projects',
  beforeLoad: () => {
    throw redirect({ to: '/projects' })
  },
})

// Intake Team routes
const intakeTeamRoute = createRoute({
  getParentRoute: () => dashboardRolesRoute,
  id: '_intakeTeam',
  beforeLoad: ({ context }) => requireRole(context, ['intake_team']),
  component: Outlet,
})

const intakeQueueRoute = createRoute({
  getParentRoute: () => intakeTeamRoute,
  path: '/intake',
  head: () => ({
    meta: [{ title: formatTitle('Intake Queue') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/IntakeQueue')),
  pendingComponent: MyTeamProjectsPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(assignedProjectsDetailedOptions()).catch(() => {})
  },
})

const intakeProjectDetailRoute = createRoute({
  getParentRoute: () => intakeTeamRoute,
  path: '/intake/$projectId',
  head: () => ({
    meta: [{ title: formatTitle('Intake Project') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/IntakeProjectDetail')),
  pendingComponent: MyTeamProjectsPending,
  loader: async ({ context: { queryClient, auth } }) => {
    if (!auth.isAuthenticated) return
    await queryClient.ensureQueryData(assignedProjectsDetailedOptions()).catch(() => {})
  },
})

// ─── Settings routes ──────────────────────────────────────────────
const settingsLayoutRoute = createRoute({
  getParentRoute: () => iconRailRoute,
  id: '_settingsLayout',
  beforeLoad: ({ context }) =>
    requireRole(context, [
      'founder',
      'doer',
      'doing_team',
      'intake_team',
      'admin',
      'escalation_officer',
      'dsl',
    ]),
  component: SettingsLayout,
})

const settingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings',
  head: () => ({
    meta: [{ title: formatTitle('Settings') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/Settings')),
  pendingComponent: SettingsPending,
})

// Where the desktop agent's tray "Update required" item sends the doer. The
// API hands the agent this URL alongside the staleness signal, per environment
// via AGENT_UPDATE_INFO_URL, with the agent's compiled-in constant as a
// fallback — so changing this path means updating that variable (and any baked
// fallback), not shipping a new agent build. It is still a published contract:
// change it and every stale agent pointed here by an already-deployed API
// lands on a 404 until the variable follows.
// Parented under settingsLayoutRoute (not settingsRoute) because a doer with
// no assigned founder is restricted to Home + Settings and those two never
// redirect (see router.tsx:466-468) — the doers most likely to be running a
// stale agent must be able to reach this page.
const settingsActivityAgentRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/activity-agent',
  head: () => ({
    meta: [{ title: formatTitle('Activity Agent') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/ActivityAgent')),
  pendingComponent: ActivityAgentPending,
})

// Under settingsLayoutRoute for the same reason as the Activity Agent page: a
// doer with no founder is restricted to Home + Settings. Flag-gated by
// useFeatureFlagRouteGuard; the API enforces the flag too. ClickUp 86d40rj6v.
const settingsMyActivityRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/my-activity',
  head: () => ({
    meta: [{ title: formatTitle('My Activity') }],
  }),
  component: lazyRouteComponent(() => import('@/pages/MyActivity')),
  pendingComponent: MyActivityPending,
})

// ─── Route tree ──────────────────────────────────────────────────
const routeTree = rootRoute.addChildren([
  indexRoute,
  callbackRoute,
  forgotPasswordRoute,
  authRedirectRoute,
  logoutRoute,
  signupRoute,
  adminPlaygroundRedirectRoute,
  adminPlaygroundRunsRedirectRoute,
  adminPlaygroundRunRedirectRoute,
  adminGroupsRedirectRoute,
  adminGroupDetailRedirectRoute,
  protectedRoute.addChildren([
    iconRailRoute.addChildren([
      messagesRoute,
      messagesContactRoute,
      feedbackRoute,
      feedbackItemRoute,
      appLayoutRoute.addChildren([
        activityViewerRoute.addChildren([adminActivityLogRoute, adminActivitySummaryRoute]),
        adminOnlyRoute.addChildren([
          adminRedirectRoute,
          adminStatsRoute,
          adminActivitySearchRoute,
          adminActivityClassificationsRoute,
          adminAgentReleasesRoute,
          adminUsersRoute,
          adminUserDetailRoute,
          adminDslAgentRoute,
          adminCoachingSessionsRoute,
          adminCoachingSessionDetailRoute,
          adminEscalationsRoute,
          adminEscalationDetailRoute,
          adminFounderBriefRoute,
          adminCategoriesRoute,
          adminCategoryDetailRoute,
          adminPlaybooksRoute,
          // Editor tabs. `$slug` must come AFTER the literal '/admin/playbooks'
          // route so the list is not swallowed by the param route.
          adminPlaybookDetailsRoute,
          adminPlaybookFounderPageRoute,
          adminPlaybookDoerPageRoute,
          adminPlaybookMediaRoute,
          adminPlaybookPublishRoute,
          adminSchemaRoute,
          adminDoerSchemaRoute,
          adminFeedbackRoute,
          adminFeedbackDetailRoute,
          adminTeamsRoute,
          adminTeamDetailRoute,
          adminDoerGroupsRoute,
          adminDoerGroupDetailRoute,
          adminGroupChatsRoute,
          adminGroupChatDetailRoute,
          adminFeatureFlagsRoute,
          adminFeatureFlagDetailRoute,
          adminCoachingCallsRoute,
          adminCoachingCallRecordingsRoute,
          adminMasterclassVaultRoute,
          // Editor tabs. `$courseId` must come AFTER the literal
          // '/admin/masterclass-vault' route so the list is not swallowed.
          adminMasterclassCourseDetailRoute,
          adminMasterclassLessonsRoute,
          adminMasterclassPublishRoute,
        ]),
        notificationsRoute,
        deviceNotificationsRoute,
        playbookDetailAccessRoute.addChildren([playbookDetailRoute]),
        masterclassLessonAccessRoute.addChildren([masterclassLessonRoute]),
        dashboardRolesRoute.addChildren([
          founderOnlyRoute.addChildren([
            founderIntakeRoute,
            startHereRoute,
            launchDayRoute,
            techStackFinderRoute,
            coachingCallsRoute,
            coachingCallReplayRoute,
            doersUniversityRoute,
            masterclassVaultRoute,
            foundationsRoute,
            foundersLoungeRoute,
            foundersLoungeArticleRoute,
            kickOffCallRoute,
          ]),
          founderDoerRoute.addChildren([
            founderPlaybookAccessRoute.addChildren([doingBlueprintRoute, playbooksRoute]),
            businessBriefRoute,
            launchChecklistRoute,
            doerProfileRoute,
            filesRoute,
            fileFolderRoute,
            workflowsRoute,
            workflowSmcTriggerRoute,
            workflowSmcRunsListRoute,
            workflowSmcRunStatusRoute,
            workflowSmcReviewRoute,
            tasksRoute.addChildren([
              tasksIndexRoute,
              tasksListRoute,
              tasksKanbanRoute,
              tasksCalendarRoute,
              tasksCalendarDateRoute,
              tasksCollectionsRoute,
              tasksProjectsLegacyRoute,
              tasksProjectIndexRoute,
              tasksProjectListRoute,
              tasksProjectKanbanRoute,
              tasksProjectCalendarRoute,
              tasksProjectCalendarDateRoute,
            ]),
            taskCreateRoute,
            taskCollectionDetailRoute,
            taskProjectDetailLegacyRoute,
            taskDetailRoute,
          ]),
          doerOnlyRoute.addChildren([
            doerHomeRoute,
            teamsRoute,
            preSessionPrepRoute,
            aiCoachingRoute,
            submitWorkRoute,
            loyaltyRoute,
            coachingSessionHistoryRoute,
            coachingSessionDetailRoute,
          ]),
          doingTeamRoute.addChildren([projectsRoute, projectDetailRoute, legacyMyTeamProjectsRoute]),
          intakeTeamRoute.addChildren([intakeQueueRoute, intakeProjectDetailRoute]),
        ]),
        escalationOfficerRoute.addChildren([
          escalationConsoleRoute,
          escalationDetailRoute,
          escalationSessionRoute,
          escalationDoerRoute,
        ]),
        cxRiskRoute.addChildren([cxRiskDashboardRoute]),
        weeklyReviewQueueGateRoute.addChildren([weeklyReviewQueueRoute, weeklyReviewDetailRoute]),
        groupsLayoutRoute.addChildren([groupsRoute, groupMentionsRoute, groupRepliesRoute, groupDetailRoute]),
      ]),
      settingsLayoutRoute.addChildren([settingsRoute, settingsActivityAgentRoute, settingsMyActivityRoute]),
    ]),
  ]),
])

// ─── Router instance ─────────────────────────────────────────────
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingMs: 300,
  defaultPendingMinMs: 200,
  scrollRestoration: true,
  scrollToTopSelectors: ['[data-scroll-restoration-id="dashboard-scroll"]'],
  defaultNotFoundComponent: NotFound,
  context: undefined!,
})

// ─── Type registration ───────────────────────────────────────────
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
  interface StaticDataRouteOption {
    fullHeight?: boolean
    /** Which tab of the /tasks board this route is. See `tasksView`. */
    tasksTab?: TasksTab
  }
}

// Re-export route objects for typed useParams/useSearch
export { adminSchemaRoute, callbackRoute }
